import React, { useEffect, useState } from 'react';
import * as d3 from 'd3';

const MitreHeatmap = ({ tactics = [], techniques = [], priorities = [] }) => {
    const [heatmapData, setHeatmapData] = useState([]);
    const [loading, setLoading] = useState(true);

    // MITRE ATT&CK Tactics (simplified mapping)
    const mitreMatrix = {
        'TA0001': { name: 'Initial Access', techniques: ['T1566', 'T1190', 'T1133', 'T1078'] },
        'TA0002': { name: 'Execution', techniques: ['T1059', 'T1053', 'T1204', 'T1047'] },
        'TA0003': { name: 'Persistence', techniques: ['T1053', 'T1547', 'T1078', 'T1136'] },
        'TA0004': { name: 'Privilege Escalation', techniques: ['T1548', 'T1055', 'T1068', 'T1134'] },
        'TA0005': { name: 'Defense Evasion', techniques: ['T1055', 'T1027', 'T1070', 'T1036'] },
        'TA0006': { name: 'Credential Access', techniques: ['T1110', 'T1003', 'T1558', 'T1212'] },
        'TA0007': { name: 'Discovery', techniques: ['T1083', 'T1057', 'T1018', 'T1082'] },
        'TA0008': { name: 'Lateral Movement', techniques: ['T1021', 'T1080', 'T1550', 'T1563'] },
        'TA0009': { name: 'Collection', techniques: ['T1005', 'T1039', 'T1025', 'T1074'] },
        'TA0010': { name: 'Exfiltration', techniques: ['T1041', 'T1048', 'T1052', 'T1567'] },
        'TA0011': { name: 'Command and Control', techniques: ['T1071', 'T1573', 'T1090', 'T1105'] },
        'TA0040': { name: 'Impact', techniques: [] }
    };

    useEffect(() => {
        const processHeatmapData = () => {
            console.log('MitreHeatmap received tactics:', tactics);
            console.log('MitreHeatmap received techniques:', techniques);
            console.log('MitreHeatmap received priorities:', priorities);
            
            const data = [];
            
            // Ensure arrays are defined
            const safeTactics = tactics || [];
            const safeTechniques = techniques || [];
            const safePriorities = priorities || [];
            
            // Process entities to extract tactics and techniques
            const tacticMap = new Map();
            const techniqueMap = new Map();

            console.log('Processing entities:', {
                tacticsCount: tactics.length,
                techniquesCount: techniques.length,
                prioritiesCount: priorities.length
            });

            // Default MITRE tactics for visualization
            const defaultTactics = [
                { id: 'TA0001', name: 'Initial Access' },
                { id: 'TA0002', name: 'Execution' },
                { id: 'TA0003', name: 'Persistence' },
                { id: 'TA0004', name: 'Privilege Escalation' },
                { id: 'TA0005', name: 'Defense Evasion' },
                { id: 'TA0006', name: 'Credential Access' },
                { id: 'TA0007', name: 'Discovery' },
                { id: 'TA0008', name: 'Lateral Movement' },
                { id: 'TA0009', name: 'Collection' },
                { id: 'TA0010', name: 'Exfiltration' },
                { id: 'TA0011', name: 'Command and Control' },
                { id: 'TA0040', name: 'Impact' }
            ];

            // Process tactics or use defaults
            if (tactics && Array.isArray(tactics) && tactics.length > 0) {
                tactics.forEach(tactic => {
                    console.log('Processing tactic:', tactic);
                    let tacticId = null;
                    let tacticName = null;
                    
                    if (typeof tactic === 'string') {
                        // Extract tactic ID from string (e.g., "TA0001" or "TA0001: Initial Access")
                        const match = tactic.match(/TA\d{4}/i);
                        if (match) {
                            tacticId = match[0].toUpperCase();
                            tacticName = tactic.replace(/.*:/, '').trim() || tacticId;
                        }
                    } else if (tactic && typeof tactic === 'object') {
                        tacticId = tactic.tacticId || tactic.id;
                        tacticName = tactic.name || tacticId;
                    }
                    
                    if (tacticId) {
                        tacticMap.set(tacticId, tacticName);
                        console.log('Added tactic to map:', { tacticId, tacticName });
                    }
                });
            } else {
                // Use default tactics if none provided
                defaultTactics.forEach(tactic => {
                    tacticMap.set(tactic.id, tactic.name);
                });
                console.log('Using default tactics for visualization');
            }

            // Extract technique IDs from the entities
            let techniqueIds = safeTechniques.map(technique => {
                if (typeof technique === 'string') {
                    const match = technique.match(/T\d{4}(?:\.\d{3})?/);
                    return match ? match[0] : null;
                } else if (technique && typeof technique === 'object') {
                    return technique.techniqueId || technique.id || null;
                }
                return null;
            }).filter(Boolean);
            
            // Add sample techniques if none provided for demonstration
            if (techniqueIds.length === 0) {
                techniqueIds = ['T1566', 'T1055', 'T1003', 'T1082', 'T1071'];
                console.log('Using sample techniques for visualization');
            }
            
            console.log('Extracted technique IDs:', techniqueIds);

            // Extract tactic IDs for comparison
            const tacticIds = Array.from(tacticMap.keys());
            console.log('Extracted tactic IDs:', tacticIds);

            // Calculate priority score for heatmap intensity
            const getPriorityScore = () => {
                if (!priorities || !Array.isArray(priorities) || priorities.length === 0) return 2; // Default medium priority
                
                let maxScore = 0;
                priorities.forEach(priority => {
                    let score = 1;
                    if (typeof priority === 'string') {
                        if (priority.toLowerCase().includes('critical')) score = 4;
                        else if (priority.toLowerCase().includes('high')) score = 3;
                        else if (priority.toLowerCase().includes('medium')) score = 2;
                        else if (priority.toLowerCase().includes('low')) score = 1;
                    } else if (priority && typeof priority === 'object') {
                        score = priority.score || priority.level || 1;
                    }
                    maxScore = Math.max(maxScore, score);
                });
                return maxScore || 2; // Default to medium if no score
            };

            const priorityScore = getPriorityScore();
            console.log('Priority score calculated:', priorityScore);

            // Create heatmap data
            Object.entries(mitreMatrix).forEach(([tacticId, tacticInfo]) => {
                const tacticPresent = tacticIds.includes(tacticId);
                const tacticScore = tacticPresent ? 1 : 0;

                tacticInfo.techniques.forEach(techniqueId => {
                    const techniquePresent = techniqueIds.some(id => id.startsWith(techniqueId));
                    let intensity = 0;

                    if (tacticPresent && techniquePresent) {
                        intensity = 0.8 + (priorityScore >= 4 ? 0.2 : 0); // Critical priority adds extra intensity
                    } else if (tacticPresent || techniquePresent) {
                        intensity = 0.4 + (priorityScore >= 3 ? 0.1 : 0); // High priority adds extra intensity
                    } else if (priorityScore >= 2) {
                        intensity = 0.1; // Medium priority shows minimal intensity
                    }

                    data.push({
                        tactic: tacticId,
                        tacticName: tacticInfo.name,
                        technique: techniqueId,
                        intensity: Math.min(intensity, 1),
                        present: tacticPresent || techniquePresent
                    });
                });
            });

            console.log('Generated heatmap data:', data);
            console.log('Active cells:', data.filter(d => d.intensity > 0).length);
            
            setHeatmapData(data);
            setLoading(false);
        };

        processHeatmapData();
    }, [tactics, techniques, priorities]);

    const renderHeatmap = () => {
        if (heatmapData.length === 0) return null;

        const tacticNames = [...new Set(heatmapData.map(d => d.tacticName))];
        const techniqueIds = [...new Set(heatmapData.map(d => d.technique))];

        const cellSize = 40;
        const margin = { top: 100, right: 50, bottom: 50, left: 150 };
        const width = techniqueIds.length * cellSize + margin.left + margin.right;
        const height = tacticNames.length * cellSize + margin.top + margin.bottom;

        const colorScale = d3.scaleSequential(d3.interpolateReds)
            .domain([0, 1]);

        return (
            <svg width={width} height={height} className="mitre-heatmap-svg">
                {/* Tactic labels */}
                {tacticNames.map((tactic, i) => (
                    <text
                        key={tactic}
                        x={margin.left - 10}
                        y={margin.top + i * cellSize + cellSize / 2}
                        textAnchor="end"
                        dominantBaseline="middle"
                        fontSize="12"
                        fill="#333"
                    >
                        {tactic}
                    </text>
                ))}

                {/* Technique labels */}
                {techniqueIds.map((technique, i) => (
                    <text
                        key={technique}
                        x={margin.left + i * cellSize + cellSize / 2}
                        y={margin.top - 10}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="10"
                        fill="#333"
                        transform={`rotate(-45, ${margin.left + i * cellSize + cellSize / 2}, ${margin.top - 10})`}
                    >
                        {technique}
                    </text>
                ))}

                {/* Heatmap cells */}
                {heatmapData.map((d, i) => {
                    const tacticIndex = tacticNames.indexOf(d.tacticName);
                    const techniqueIndex = techniqueIds.indexOf(d.technique);
                    
                    return (
                        <rect
                            key={i}
                            x={margin.left + techniqueIndex * cellSize}
                            y={margin.top + tacticIndex * cellSize}
                            width={cellSize - 1}
                            height={cellSize - 1}
                            fill={d.intensity > 0 ? colorScale(d.intensity) : '#f8f9fa'}
                            stroke="#fff"
                            strokeWidth="1"
                            className="mitre-cell"
                            title={`${d.tacticName} - ${d.technique}: ${(d.intensity * 100).toFixed(0)}%`}
                        />
                    );
                })}
            </svg>
        );
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Generating heatmap...</span>
                    </div>
                    <p className="text-muted">Processing MITRE ATT&CK data...</p>
                </div>
            </div>
        );
    }

    const activeCells = heatmapData.filter(d => d.intensity > 0).length;
    const totalCells = heatmapData.length;

    return (
        <div className="card">
            <div className="card-header">
                <h6 className="mb-0">
                    <i className="fas fa-fire me-2"></i>
                    MITRE ATT&CK Heatmap
                    <span className="badge bg-danger ms-2">
                        {activeCells}/{totalCells} techniques
                    </span>
                </h6>
            </div>
            <div className="card-body">
                {activeCells === 0 ? (
                    <div className="text-center py-5">
                        <i className="fas fa-shield-alt fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted">No MITRE ATT&CK Data</h5>
                        <p className="text-muted">
                            No MITRE tactics or techniques were identified in this document.
                        </p>
                    </div>
                ) : (
                    <div>
                        <div className="mb-3">
                            <small className="text-muted">
                                This heatmap shows the MITRE ATT&CK tactics and techniques identified in the document.
                                Darker colors indicate higher threat levels or more frequent mentions.
                            </small>
                        </div>
                        
                        <div className="overflow-auto">
                            {renderHeatmap()}
                        </div>
                        
                        {/* Legend */}
                        <div className="mitre-legend mt-3">
                            <div className="legend-item">
                                <div className="legend-color" style={{ backgroundColor: '#f8f9fa' }}></div>
                                <span>Not Present</span>
                            </div>
                            <div className="legend-item">
                                <div className="legend-color" style={{ backgroundColor: '#ffcccc' }}></div>
                                <span>Low</span>
                            </div>
                            <div className="legend-item">
                                <div className="legend-color" style={{ backgroundColor: '#ff6666' }}></div>
                                <span>Medium</span>
                            </div>
                            <div className="legend-item">
                                <div className="legend-color" style={{ backgroundColor: '#cc0000' }}></div>
                                <span>High</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="card-footer">
                <div className="row">
                    <div className="col-md-6">
                        <small className="text-muted">
                            <i className="fas fa-info-circle me-1"></i>
                            Tactics: {tactics.length} | Techniques: {techniques.length}
                        </small>
                    </div>
                    <div className="col-md-6 text-end">
                        <small className="text-muted">
                            Coverage: {totalCells > 0 ? ((activeCells / totalCells) * 100).toFixed(1) : 0}%
                        </small>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MitreHeatmap;
